import React from "react";
import { motion } from "framer-motion";
import {
  FaStar,
  FaShieldHalved,
  FaGlobe,
  FaBriefcase,
  FaGraduationCap,
  FaHeart,
} from "react-icons/fa6";
import { MX, BR, US } from "country-flag-icons/react/3x2";

const TestimonialGrid = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-auto md:auto-rows-[280px] gap-6 max-w-7xl mx-auto relative z-10 w-full">
      {/* Row 1: Santiago (2 cols) | Stat 1 (1 col) | Stat 2 (1 col) */}

      {/* Santiago Testimonial (2 cols) */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-white rounded-4xl p-8 flex flex-col justify-between md:col-span-2 border border-grey-eb shadow-xs hover:shadow-xl hover:border-blue-00/20 transition-all duration-300 relative group overflow-hidden"
      >
        <div className="flex gap-1 text-amber-fe mb-4 text-sm">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} />
          ))}
        </div>
        <p className="text-black-31 text-base md:text-[17px] leading-relaxed font-normal grow">
          "After my first H-1B was denied due to documentation issues,
          SeamlessVisa helped me understand exactly what went wrong and rebuilt
          my application from the ground up. Approved on the next cycle."
        </p>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-grey-eb">
          {/* Left: Flags/route pill */}
          <div className="flex items-center gap-2 shrink-0 text-xs font-semibold text-dark-6b bg-white-f3 px-3 py-1.5 rounded-full border border-grey-eb shadow-2xs">
            <MX name="Mexico" className="w-4 h-3 rounded-[1px] object-cover" />
            <span aria-hidden className="text-dark-6b/50">
              →
            </span>
            <US
              name="United States"
              className="w-4 h-3 rounded-[1px] object-cover"
            />
            <span className="text-[10px] text-dark-6b uppercase font-bold tracking-wider ml-1.5">
              H-1B Pathway
            </span>
          </div>

          {/* Right: Customer Profile */}
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="font-bold text-black-1a text-sm">Santiago M.</p>
              <p className="text-dark-6b text-xs">
                Data Scientist, San Francisco
              </p>
            </div>
            <div className="h-10 w-10 bg-amber-fe/10 rounded-full flex items-center justify-center text-amber-fF font-bold text-sm shrink-0 border border-amber-fe/20">
              SM
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stat Card 1: Success Rate */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-blue-ea rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-blue-100 hover:shadow-lg transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-blue-100 shadow-2xs">
            <FaStar className="text-blue-00 text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-blue-00/80">
            Approved
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-blue-00 mb-1.5">
            98.4%
          </h3>
          <p className="text-black-1a text-sm font-semibold">Approval Rate</p>
          <p className="text-dark-6b text-xs mt-0.5">
            Immigrant & skilled visas
          </p>
        </div>
      </motion.div>

      {/* Stat Card 2: Processed Cases */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-blue-00 rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-blue-00 hover:shadow-lg transition-all duration-300 text-white"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 backdrop-blur-md">
            <FaGlobe className="text-white text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-blue-200">
            Global
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-white mb-1.5">
            10k+
          </h3>
          <p className="text-blue-50 text-sm font-semibold">Successful Cases</p>
          <p className="text-blue-100/80 text-xs mt-0.5">Processed globally</p>
        </div>
      </motion.div>

      {/* Row 2: Stat 3 (1 col) | Flavia (2 cols) | Stat 4 (1 col) */}

      {/* Stat Card 3: Corporate Relocations */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-blue-f0 rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-blue-100 hover:shadow-lg transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-blue-100 shadow-2xs">
            <FaBriefcase className="text-blue-02 text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-blue-02/80">
            Mobility
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-blue-02 mb-1.5">
            120+
          </h3>
          <p className="text-black-1a text-sm font-semibold">
            Corporate Partners
          </p>
          <p className="text-dark-6b text-xs mt-0.5">
            Organizational relocation solutions
          </p>
        </div>
      </motion.div>

      {/* Flavia Testimonial (2 cols) */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-white rounded-4xl p-8 flex flex-col justify-between md:col-span-2 border border-grey-eb shadow-xs hover:shadow-xl hover:border-blue-00/20 transition-all duration-300 relative group overflow-hidden"
      >
        <div className="flex gap-1 text-amber-fe mb-4 text-sm">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} />
          ))}
        </div>
        <p className="text-black-31 text-base md:text-[17px] leading-relaxed font-normal grow">
          "I had never applied for a US visa before and felt overwhelmed by the
          process. SeamlessVisa walked me through every step clearly and calmly.
          My B-2 was approved and my trip was everything I hoped for."
        </p>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-grey-eb">
          {/* Left: Flags/route pill */}
          <div className="flex items-center gap-2 shrink-0 text-xs font-semibold text-dark-6b bg-white-f3 px-3 py-1.5 rounded-full border border-grey-eb shadow-2xs">
            <BR name="Brazil" className="w-4 h-3 rounded-[1px] object-cover" />
            <span aria-hidden className="text-dark-6b/50">
              →
            </span>
            <US
              name="United States"
              className="w-4 h-3 rounded-[1px] object-cover"
            />
            <span className="text-[10px] text-dark-6b uppercase font-bold tracking-wider ml-1.5">
              B-2 Visitor Path
            </span>
          </div>

          {/* Right: Customer Profile */}
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="font-bold text-black-1a text-sm">Flavia S.</p>
              <p className="text-dark-6b text-xs">
                Salvador → New York Tourist Visit
              </p>
            </div>
            <div className="h-10 w-10 bg-blue-ea rounded-full flex items-center justify-center text-blue-00 font-bold text-sm shrink-0 border border-blue-100">
              FS
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stat Card 4: Temporary Visas */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-amber-fe/10 rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-amber-fe/20 hover:shadow-lg transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-amber-fe/10 shadow-2xs">
            <FaGraduationCap className="text-amber-fF text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-amber-fF/80">
            Student/B-2
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-amber-fF mb-1.5">
            5.8k+
          </h3>
          <p className="text-black-1a text-sm font-semibold">Temporary Visas</p>
          <p className="text-dark-6b text-xs mt-0.5">
            Visitor & student visa pathways
          </p>
        </div>
      </motion.div>

      {/* Row 3: Stat 5 (1 col) | Stat 6 (1 col) | Michael (2 cols) */}

      {/* Stat Card 5: Family Sponsorship */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-teal-00/10 rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-teal-00/20 hover:shadow-lg transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-teal-00/10 shadow-2xs">
            <FaHeart className="text-teal-1f text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-teal-1f/80">
            Immigrant
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-teal-1f mb-1.5">
            4.2k+
          </h3>
          <p className="text-black-1a text-sm font-semibold">
            Families Reunited
          </p>
          <p className="text-dark-6b text-xs mt-0.5">
            Family sponsorship petitions
          </p>
        </div>
      </motion.div>

      {/* Stat Card 6: Unexpected Charges */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-white-f3 rounded-4xl p-6 flex flex-col justify-between col-span-1 shadow-xs border border-grey-eb hover:shadow-lg transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-grey-eb shadow-2xs">
            <FaShieldHalved className="text-black-1a text-lg" />
          </div>
          <div className="font-spartan font-bold text-xs uppercase tracking-widest text-black-31/80">
            Trust
          </div>
        </div>
        <div className="mt-4">
          <h3 className="text-4xl lg:text-5xl font-light font-spartan text-black-1a mb-1.5">
            $0
          </h3>
          <p className="text-black-31 text-sm font-semibold">Unexpected Fees</p>
          <p className="text-dark-6b text-xs mt-0.5">
            100% transparent agreements
          </p>
        </div>
      </motion.div>

      {/* Michael Testimonial (2 cols) */}
      <motion.div
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="bg-white rounded-4xl p-8 flex flex-col justify-between md:col-span-2 border border-grey-eb shadow-xs hover:shadow-xl hover:border-blue-00/20 transition-all duration-300 relative group overflow-hidden"
      >
        <div className="flex gap-1 text-amber-fe mb-4 text-sm">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} />
          ))}
        </div>
        <p className="text-black-31 text-base md:text-[17px] leading-relaxed font-normal grow">
          "As a small landscaping business, handling H-2B applications every
          season was a real burden. SeamlessVisa now manages the whole process
          for us. Reliable, professional, and worth every penny."
        </p>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-grey-eb">
          {/* Left: Flags/route pill */}
          <div className="flex items-center gap-2 shrink-0 text-xs font-semibold text-dark-6b bg-white-f3 px-3 py-1.5 rounded-full border border-grey-eb shadow-2xs">
            <US
              name="United States"
              className="w-4 h-3 rounded-[1px] object-cover"
            />
            <span className="text-[10px] text-dark-6b uppercase font-bold tracking-wider ml-1.5">
              H-2B Sponsor
            </span>
          </div>

          {/* Right: Customer Profile */}
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="font-bold text-gray-900 text-sm">Michael R.</p>
              <p className="text-gray-500 text-xs">Business Owner, Montana</p>
            </div>
            <div className="h-10 w-10 bg-purple-a8/10 rounded-full flex items-center justify-center text-purple-a8 font-bold text-sm shrink-0 border border-purple-a8/20">
              MR
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Testimonials = () => {
  return (
    <section className="bg-linear-to-b from-white to-blue-ea! py-24 sm:py-32 overflow-hidden flex flex-col items-center border-t border-gray-50">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mb-16 relative w-full text-center">
        <span className="text-xs font-normal text-blue-00 uppercase tracking-widest mb-4 block">
          ✦ Testimonials & Impact
        </span>
        <h2 className="font-spartan font-light tracking-tight text-black-1a text-4xl sm:text-5xl max-w-3xl mx-auto leading-tight">
          Real{" "}
          <span className="text-blue-00 italic mx-1">Results</span>{" "}
          Real People
        </h2>
      </div>

      <div className="px-6 w-full max-w-7xl">
        <TestimonialGrid />
      </div>
    </section>
  );
};

export default Testimonials;
